import React, { useRef } from "react";
import type { ComboBoxProps } from "@react-types/combobox";
import { useFilter } from "@react-aria/i18n";
import { useComboBoxState, useSearchFieldState } from "react-stately";
import { useComboBox } from "@react-aria/combobox";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import Popover from "../../design-system-components/Popover/Popover";
import ListBox from "../ListBox/ListBox";
import { useButton } from "@react-aria/button";
import { useSearchField } from "@react-aria/searchfield";
import cn from "classnames";

import "./SearchAutocomplete.scss";
import classNames from "classnames";

type Props<T> = {
    /** @default false */
    hideLabel?: boolean;
    className?: string;
} & ComboBoxProps<T>;

const SearchAutocomplete = <T extends object>({
    hideLabel = false,
    className,
    ...props
}: Props<T>) => {
    const { contains } = useFilter({ sensitivity: "base" });
    const state = useComboBoxState({ ...props, defaultFilter: contains });

    const inputRef = useRef<HTMLInputElement>(null);
    const listBoxRef = useRef<HTMLUListElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const { inputProps, listBoxProps, labelProps } = useComboBox(
        {
            ...props,
            inputRef,
            listBoxRef,
            popoverRef,
        },
        state
    );

    const searchProps = {
        label: props.label,
        value: state.inputValue,
        onChange: (value: string) => state.setInputValue(value),
    };

    const searchState = useSearchFieldState(searchProps);
    const { clearButtonProps } = useSearchField(
        searchProps,
        searchState,
        inputRef
    );
    const clearButtonRef = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(clearButtonProps, clearButtonRef);
    const outerRef = useRef<HTMLDivElement>(null);

    return (
        <div className={classNames("search-autocomplete", className)}>
            {props.label && (
                <label
                    {...labelProps}
                    className={cn({
                        "search-autocomplete__label--hidden": hideLabel,
                    })}
                >
                    {props.label}
                </label>
            )}
            <div
                className={cn("search-autocomplete__input", {
                    "search-autocomplete__input--focused": state.isFocused,
                })}
                ref={outerRef}
            >
                <Icon
                    className="search-autocomplete__input__icon"
                    path="search"
                    size="medium"
                    color="var(--text-main-default)"
                />
                <input {...inputProps} ref={inputRef} />
                {!!state.inputValue && (
                    <button
                        {...buttonProps}
                        ref={clearButtonRef}
                        className="search-autocomplete__input__reset"
                    >
                        <Icon
                            className="search-autocomplete__input__reset__icon"
                            path="close"
                            size="medium"
                            color="var(--black)"
                        />
                    </button>
                )}
            </div>
            {state.isOpen && (
                <Popover
                    className="search-autocomplete__suggestions"
                    popoverRef={popoverRef}
                    triggerRef={inputRef}
                    state={state}
                    isNonModal
                    placement="bottom start"
                    containerPadding={0}
                    parentContainerRef={outerRef}
                >
                    <ListBox
                        {...listBoxProps}
                        listBoxRef={listBoxRef}
                        state={state}
                    />
                </Popover>
            )}
        </div>
    );
};

export default SearchAutocomplete;
